import React, { useMemo } from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCourseVersioning, VersionEntry } from './CourseVersioningContext';
import { formatDate } from '../../../../../../../../utils/formatDate';
import { Bone } from '../../../../../../../../components/ui/DashboardSkeletons';

/**
 * What the panel reads. The Designer's CourseVersioningContext by default;
 * any other source with the same item shape (the AIMD wizard's generation
 * versions) can be passed explicitly.
 */
export interface VersionHistorySource {
  entries: VersionEntry[];
  entriesLoading: boolean;
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  previewEntry: { id: string } | null;
  previewLoadingId: string | null;
  openPreview: (id: string) => void;
  resume: () => void;
}

interface VersionHistoryDrawerProps {
  /** Defaults to the Designer's context. */
  source?: VersionHistorySource;
  /** The bullet hints under the header; defaults to the Designer's draft copy. */
  hints?: string[];
  currentLabel?: string;
  currentHint?: string;
}

const DESIGNER_HINTS = [
  'Your draft saves automatically as anyone edits.',
  'Click a version to preview it. Nothing changes until you restore.',
  'Restoring updates your draft only. Learners see changes when you publish.'
];

const KIND_STYLE: Record<VersionEntry['kind'], { bg: string; fg: string }> = {
  published: { bg: 'rgba(76, 175, 132, 0.16)', fg: '#6FD1A6' },
  autosave: { bg: '#2A2A2A', fg: '#B6B6B6' },
  before_restore: { bg: 'rgba(247, 148, 32, 0.16)', fg: '#F7A94A' },
  before_ai: { bg: 'rgba(247, 148, 32, 0.16)', fg: '#F7A94A' }
};

function dayLabel(d: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return formatDate(d);
}

const timeLabel = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

/** Version History panel (PRD A5). Persistent so the preview stays visible beside it. */
export function VersionHistoryDrawer({
  source,
  hints = DESIGNER_HINTS,
  currentLabel = 'Current Draft',
  currentHint = 'What you’re editing now'
}: VersionHistoryDrawerProps = {}) {
  const ctx = useCourseVersioning();
  const v: VersionHistorySource | null = source ?? ctx;

  const groups = useMemo(() => {
    const out: { day: string; items: VersionEntry[] }[] = [];
    (v?.entries || []).forEach((e) => {
      const day = dayLabel(e.at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(e);
      else out.push({ day, items: [e] });
    });
    return out;
  }, [v?.entries]);

  if (!v) return null;
  const previewId = v.previewEntry?.id;
  const selectedId = v.previewLoadingId || previewId;

  const rowSx = (selected: boolean) => ({
    width: '100%',
    minHeight: 52,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 1,
    textAlign: 'left' as const,
    cursor: 'pointer',
    borderRadius: '8px',
    border: `1px solid ${selected ? '#F79420' : '#2A2A2A'}`,
    background: selected ? 'rgba(247, 148, 32, 0.08)' : 'transparent',
    color: '#FDFDFD',
    fontFamily: 'Inter',
    '&:hover': { borderColor: selected ? '#F79420' : '#404040' }
  });

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={v.panelOpen}
      PaperProps={{
        sx: {
          width: 380,
          background: '#1A1A1A',
          borderLeft: '1px solid #2A2A2A',
          color: '#FDFDFD',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid #2A2A2A' }}>
        <Typography sx={{ flex: 1, fontSize: 17, fontWeight: 700, fontFamily: 'Inter' }}>Version history</Typography>
        <IconButton aria-label="Close Version History" onClick={() => v.setPanelOpen(false)} sx={{ color: '#B6B6B6' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        component="ul"
        sx={{
          m: 0,
          px: 2.5,
          py: 1.5,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          background: '#161616',
          borderBottom: '1px solid #2A2A2A'
        }}
      >
        {hints.map((line) => (
          <Box
            component="li"
            key={line}
            sx={{ display: 'flex', gap: 1, fontSize: 12, lineHeight: '18px', fontFamily: 'Inter', color: '#B6B6B6' }}
          >
            <Box component="span" sx={{ color: '#F79420' }}>
              •
            </Box>
            {line}
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box component="button" type="button" onClick={v.resume} sx={rowSx(!previewId)}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#F79420', flexShrink: 0 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'Inter' }}>{currentLabel}</Typography>
            <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: '#868686' }}>{currentHint}</Typography>
          </Box>
        </Box>

        {v.entriesLoading && !v.entries.length && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 1 }}
            aria-busy="true"
            aria-label="Loading versions"
          >
            <Bone width={80} height={12} sx={{ mx: 1 }} />
            <Bone height={56} radius="8px" />
            <Bone height={56} radius="8px" />
            <Bone height={56} radius="8px" />
          </Box>
        )}
        {!v.entriesLoading && !v.entries.length && (
          <Typography sx={{ px: 1, fontSize: 12, fontFamily: 'Inter', color: '#868686' }}>
            No versions yet. Autosaves and publishes will show up here.
          </Typography>
        )}
        {groups.map((g) => (
          <Box key={g.day} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography
              sx={{
                px: 1,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontFamily: 'Inter',
                color: '#868686'
              }}
            >
              {g.day}
            </Typography>
            {g.items.map((e) => {
              const style = KIND_STYLE[e.kind];
              return (
                <Box
                  key={e.id}
                  component="button"
                  type="button"
                  aria-pressed={selectedId === e.id}
                  aria-busy={v.previewLoadingId === e.id}
                  onClick={() => v.openPreview(e.id)}
                  sx={rowSx(selectedId === e.id)}
                >
                  <Typography
                    sx={{
                      width: 64,
                      flexShrink: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: 'Inter',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {timeLabel(e.at)}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: 'Inter',
                      px: 1,
                      py: 0.25,
                      borderRadius: '999px',
                      whiteSpace: 'nowrap',
                      background: style.bg,
                      color: style.fg
                    }}
                  >
                    {e.label}
                  </Box>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'right',
                      fontSize: 12,
                      fontFamily: 'Inter',
                      color: '#B6B6B6',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {e.author}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}
